import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Star, Clock, ChevronLeft, ChevronRight, Heart } from "lucide-react";

import { Header } from "../layout/Header";
import { CategorySidebar, MobileCategorySidebar } from "../layout/CategorySidebar";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import FeedList from "../feed/FeedList";
import { TranslateQuotesButton } from "../feed/TranslateQuotesButton";
import { LoginRequiredState, LikedErrorState } from "./LikedStates";
import { useAppHeaderProps } from "../../lib/useAppHeaderProps";
import { CATEGORIES } from "../../lib/categories";
import { useUrlCategory, useUrlLanguage, useUrlPage, useUrlParam } from "../../lib/url-state";
import { getStoredCurrentUser } from "../../lib/app-events";
import ApiService from "../../services/api-service";
import { showErrorToast, showSuccessToast } from "../../lib/toast";
import { normalizePointsCount } from "../../lib/format";
import { ITEMS_PER_PAGE } from "../../constants/app";
import { FeedItem, Vote, Comment } from "../../types/feed";
import { CurrentUser } from "../../types/user";

type TypeFilter = "all" | "article" | "quote";
const TYPE_FILTER_OPTIONS: readonly TypeFilter[] = ["all", "article", "quote"];
type SortOption = "trending" | "recent";
const SORT_OPTIONS: readonly SortOption[] = ["trending", "recent"];

const tsOf = (value?: string | number) => {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

// Same filled-pill chip styling as the home feed's ContentTypeFilter,
// scoped down to the two content types this page ever shows.
const filterChipClass = (isActive: boolean) =>
  `shrink-0 rounded-full px-3.5 whitespace-nowrap transition-all duration-200 ${
    isActive
      ? "bg-slate-900 text-white shadow-sm shadow-slate-900/20 hover:bg-slate-900 hover:text-white dark:bg-white dark:text-slate-900 dark:hover:bg-white dark:hover:text-slate-900"
      : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-600 dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-300"
  }`;

function LikedTypeFilter({ activeType, onChange }: { activeType: TypeFilter; onChange: (type: TypeFilter) => void }) {
  const options: { value: TypeFilter; label: string }[] = [
    { value: "all", label: "All Types" },
    { value: "article", label: "Articles" },
    { value: "quote", label: "Quotes" },
  ];

  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar sm:flex-wrap sm:overflow-visible -mx-3 px-3 sm:mx-0 sm:px-0 py-0.5">
      {options.map((option) => {
          const isActive = activeType === option.value;
          return (
            <Button
              key={option.value}
              variant="ghost"
              size="sm"
              onClick={() => onChange(option.value)}
              className={filterChipClass(isActive)}
            >
              {option.label}
            </Button>
          );
        })}
    </div>
  );
}

function LikedSortToggle({ value, onChange, disabled }: { value: SortOption; onChange: (value: SortOption) => void; disabled?: boolean }) {
  const buttons: { key: SortOption; label: string; Icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }[] = [
    { key: "trending", label: "Trending", Icon: Star },
    { key: "recent", label: "Recent", Icon: Clock },
  ];

  return (
    <div className="flex flex-row-reverse flex-wrap gap-2 mb-4">
      {buttons.map(({ key, label, Icon }) => {
        const isActive = value === key;
        return (
          <Button
            key={key}
            variant={isActive ? "default" : "outline"}
            size="sm"
            onClick={() => onChange(key)}
            disabled={disabled}
            aria-pressed={isActive}
            className={`transition-all duration-200 ${
              isActive
                ? "bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200"
                : "hover:bg-slate-100 dark:text-slate-300 border-slate-300 dark:hover:bg-slate-700 dark:border-slate-600"
            }`}
          >
            <Icon className="w-4 h-4 mr-1" />
            {label}
          </Button>
        );
      })}
    </div>
  );
}

// Same numbered pagination as the home feed's Pagination component.
function LikedPagination({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}) {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages: number[] = [];
    const visiblePages = 5;
    let start = Math.max(1, currentPage - Math.floor(visiblePages / 2));
    let end = Math.min(totalPages, start + visiblePages - 1);
    if (end - start < visiblePages - 1) {
      start = Math.max(1, end - visiblePages + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="flex items-center justify-center gap-1 mt-8 select-none">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1 || isLoading}
        className="w-8 h-8 rounded border-slate-300 dark:border-slate-600"
      >
        <ChevronLeft className="w-3 h-3" />
      </Button>

      {getPageNumbers().map((page) => (
        <Button
          key={page}
          variant={page === currentPage ? "default" : "outline"}
          size="sm"
          onClick={() => onPageChange(page)}
          disabled={isLoading}
          className={`w-8 h-8 rounded transition-all duration-150 ${
            page === currentPage
              ? "bg-slate-800 text-white border-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200"
              : "border-slate-300 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-700"
          }`}
        >
          {page}
        </Button>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages || isLoading}
        className="w-8 h-8 rounded border-slate-300 dark:border-slate-600"
      >
        <ChevronRight className="w-3 h-3" />
      </Button>
    </div>
  );
}

export function LikedPage() {
  const { onOpenAuth, onOpenCreateModal } = useAppHeaderProps();

  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(getStoredCurrentUser);
  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const [typeFilter, setTypeFilter] = useUrlParam<TypeFilter>("type", "all", TYPE_FILTER_OPTIONS);
  const [sort, setSort] = useUrlParam<SortOption>("sort", "recent", SORT_OPTIONS);
  const [currentPage, setCurrentPage] = useUrlPage();
  const [activeCategory, setActiveCategory, getCategoryHref] = useUrlCategory();
  const [quoteLanguage, setQuoteLanguage, hasLangParam] = useUrlLanguage();

  // Mirrors the home feed's convention: category id 1 is the "All" entry, so
  // selecting it clears the filter rather than filtering to that one row.
  const handleCategoryChange = (categoryId: string | number) => {
    setActiveCategory(categoryId);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleUserChange = (event: Event) => {
      if (event instanceof CustomEvent) {
        setCurrentUser(event.detail ?? null);
        return;
      }
      setCurrentUser(getStoredCurrentUser());
    };
    window.addEventListener("inspirelens:userchange", handleUserChange);
    return () => window.removeEventListener("inspirelens:userchange", handleUserChange);
  }, []);

  useEffect(() => {
    if (!currentUser?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    ApiService.getLikedPosts()
      .then((res) => {
        if (cancelled) return;
        setItems(res.posts || []);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, retryToken]);

  // Mirrors FeedLayout's handleVote in App.tsx, plus: once the server confirms
  // the current user's upvote is gone, the item is no longer "liked" so it
  // drops out of this list entirely.
  const handleVote = useCallback(
    (id: number, delta: number, updatedVotes?: Vote[], postType?: string, absolutePoints?: number, updatedTimestamp?: string | number) => {
      setItems((prev) => {
        const next = prev.map((item) => {
          if (item.id !== id || (postType && item.type !== postType)) return item;
          const oldPoints = normalizePointsCount(item.points_count);
          const nextPoints =
            typeof absolutePoints === "number" && Number.isFinite(absolutePoints) ? absolutePoints : oldPoints + delta;
          const updatedItem: FeedItem = { ...item, points_count: nextPoints, points: updatedVotes || item.points };
          if (updatedTimestamp) {
            updatedItem.updated_at = updatedTimestamp;
            updatedItem.updatedAt = updatedTimestamp;
          }
          return updatedItem;
        });

        if (updatedVotes && currentUser?.id) {
          const stillLiked = updatedVotes.some(
            (vote) => Number(vote.user_id) === Number(currentUser.id) && vote.vote_type === "up"
          );
          if (!stillLiked) {
            return next.filter((item) => !(item.id === id && (!postType || item.type === postType)));
          }
        }

        return next;
      });
    },
    [currentUser?.id]
  );

  const handleAddComment = useCallback(async (itemId: number, postType: string, comment: any) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const newComment: Comment = {
          id: Date.now(),
          text: comment?.text,
          user: comment?.user,
          user_id: comment?.user_id ?? comment?.userId,
          created_at: Date.now(),
          createdBy: comment?.createdBy,
        };
        return { ...item, comments: [...(Array.isArray(item.comments) ? item.comments : []), newComment] };
      })
    );
  }, []);

  const handleDeleteComment = useCallback((itemId: number, commentId: number) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id !== itemId
          ? item
          : { ...item, comments: Array.isArray(item.comments) ? item.comments.filter((c) => c.id !== commentId) : [] }
      )
    );
  }, []);

  // Same edit/delete handling as the author, tag and profile pages. These
  // were no-ops here, so edits didn't update the card and "Delete Post" never
  // actually deleted anything.
  const handleEditPost = useCallback((post: FeedItem | number, updatedData: any) => {
    const postId = typeof post === "number" ? post : post?.id;
    if (!postId) return;

    if (!updatedData) {
      setItems((prev) => prev.filter((it) => it.id !== postId));
      return;
    }

    setItems((prev) => prev.map((it) => (it.id === postId ? { ...it, ...updatedData } : it)));
  }, []);

  const handleDeletePost = useCallback(async (postId: number, postType: string) => {
    try {
      const response = await ApiService.deleteContent(postId, postType);
      if (response?.error) throw new Error(response.error);
      setItems((prev) => prev.filter((it) => it.id !== postId));
      showSuccessToast("Post deleted successfully!");
    } catch (err) {
      showErrorToast("Failed to delete post. Please try again.");
      throw err;
    }
  }, []);

  const filteredSorted = useMemo(() => {
    let list = items;
    if (typeFilter !== "all") {
      list = list.filter((item) => item.type === typeFilter);
    }
    if (activeCategory !== "all") {
      list = list.filter((item) => String(item.category_id) === String(activeCategory));
    }

    const sorted = [...list];
    if (sort === "trending") {
      sorted.sort((a, b) => normalizePointsCount(b.points_count) - normalizePointsCount(a.points_count));
    } else {
      sorted.sort((a, b) => tsOf(b.liked_at) - tsOf(a.liked_at));
    }
    return sorted;
  }, [items, typeFilter, sort, activeCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredSorted.length / ITEMS_PER_PAGE));
  const paginatedItems = useMemo(
    () => filteredSorted.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredSorted, currentPage]
  );

  return (
    <div className="max-w-[85rem] mx-auto px-3 py-6">
      <Header currentUser={currentUser} onOpenAuth={onOpenAuth} onOpenCreateModal={onOpenCreateModal} />

      {!currentUser ? (
        <div className="mt-6">
          <LoginRequiredState onSignIn={() => onOpenAuth("login")} />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-6">
          <aside className="hidden md:block md:col-span-1">
            <div className="sticky top-6">
              <CategorySidebar activeCategory={activeCategory} onCategoryChange={handleCategoryChange} getCategoryHref={getCategoryHref} isLoading={loading} />
            </div>
          </aside>

          <main className="md:col-span-3">
          <div className="space-y-3 sm:space-y-4">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-orange-500 fill-orange-500" aria-hidden="true" />
                Liked
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
                Articles and quotes you've liked, all in one place.
              </p>
            </div>

            <div className="flex flex-row flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-orange-50 dark:bg-orange-500/10 px-3 py-1 text-sm font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">
                  <Heart className="w-3.5 h-3.5 fill-orange-500 text-orange-500" aria-hidden="true" />
                  {filteredSorted.length} liked {filteredSorted.length === 1 ? "item" : "items"}
                </div>
                <MobileCategorySidebar activeCategory={activeCategory} onCategoryChange={handleCategoryChange} getCategoryHref={getCategoryHref} isLoading={loading} />
              </div>
              {!loading && !error && totalPages > 1 && (
                <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                  Page {currentPage} of {totalPages}
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <LikedTypeFilter activeType={typeFilter} onChange={setTypeFilter} />
              <div className="flex items-center gap-2">
                <TranslateQuotesButton value={quoteLanguage} onChange={setQuoteLanguage} />
                <LikedSortToggle value={sort} onChange={setSort} disabled={loading} />
              </div>
            </div>

            {error ? (
              <LikedErrorState onRetry={() => setRetryToken((t) => t + 1)} />
            ) : loading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="w-full h-48 rounded-2xl" />
                ))}
              </div>
            ) : (
              <>
                <FeedList
                  items={paginatedItems}
                  categories={CATEGORIES}
                  currentUser={currentUser}
                  isLoading={false}
                  onVote={handleVote}
                  onAddComment={handleAddComment}
                  onDeleteComment={handleDeleteComment}
                  onEditPost={handleEditPost}
                  onDeletePost={handleDeletePost}
                  onRequireAuth={() => onOpenAuth("login")}
                  globalQuoteLanguage={hasLangParam ? quoteLanguage : undefined}
                />

                {paginatedItems.length > 0 && (
                  <LikedPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}
          </div>
          </main>
        </div>
      )}
    </div>
  );
}

export default LikedPage;
